import dayjs from 'dayjs'
import React from 'react'

import { Banner, Skeleton } from '@fourthwall/components'
import { TimezonedDateString } from '@modules/Timezone/components/TimezonedDateString'

import { isPaidTier } from '../../../../utils'
import { useTierContext } from '../../hooks/useTierContext'
import { currencyDecimalPlaces } from '../../providers/TierProvider/consts'
import { getExchangeRate } from '../../utils'
import { UpdateCurrencyConversion } from '../UpdateCurrencyConversion'
import * as S from './styled'

export const DefaultExchangeRate = () => {
  const {
    tier,
    form,
    enabledCurrencies,
    priceRates,
    updatedCurrencyConversion,
    isPriceRatesLoading,
  } = useTierContext()

  if (!tier) return null

  if (!form.values.price) return null

  if (!('pricesAutomaticallyConverted' in tier)) return

  if (!enabledCurrencies) return null

  if (!isPaidTier(tier)) return null

  if (enabledCurrencies.length === 0) return null

  if (form.values.convertInternationalPricing === false) return null

  if (updatedCurrencyConversion && form.values.price === form.initialValues.price) {
    return (
      <S.Container>
        <S.DefaultPricing>
          <p>
            Based on <TimezonedDateString date={dayjs()} format="MMM D, YYYY" /> exchange rate:
          </p>
          <p>
            {enabledCurrencies
              .map(
                (currency) => `${currency} ${getExchangeRate(currency, updatedCurrencyConversion)}`,
              )
              .join(', ')}
          </p>
          <p>
            <UpdateCurrencyConversion />
          </p>
        </S.DefaultPricing>
      </S.Container>
    )
  }

  if (
    form.values.convertInternationalPricing === true &&
    form.values.convertInternationalPricing === form.initialValues.convertInternationalPricing &&
    form.values.price === form.initialValues.price
  ) {
    const withoutUsdAndDisabledCurrencies = Object.fromEntries(
      Object.entries(tier.variants.monthly.amounts).filter(
        ([key]) =>
          key !== 'usd' &&
          enabledCurrencies.indexOf(key.toUpperCase() as (typeof enabledCurrencies)[number]) > -1,
      ),
    )

    const setAt = dayjs(tier.variants.monthly.amounts.usd.setAt).format('MMM D, YYYY')

    return (
      <S.Container>
        <S.DefaultPricing>
          <p>
            Based on <TimezonedDateString date={dayjs(setAt)} format="MMM D, YYYY" /> exchange rate:
          </p>
          <p>
            {Object.keys(withoutUsdAndDisabledCurrencies)
              .map((currency) => {
                const currencySymbol = currency as keyof typeof tier.variants.monthly.amounts

                const amount = tier.variants.monthly.amounts[currencySymbol].amount

                const exchangeRate = amount / 10 ** currencyDecimalPlaces[currencySymbol]

                return `${currency.toUpperCase()} ${exchangeRate}`
              })
              .join(', ')}
          </p>
          {form.values.price != form.initialValues.price && (
            <div style={{ margin: '16px 0' }}>
              <Banner appearance="warning">
                You have updated the price of this tier. You need to update the currency conversion
                to see the new international prices.
              </Banner>
            </div>
          )}
          <p>
            <UpdateCurrencyConversion />
          </p>
        </S.DefaultPricing>
      </S.Container>
    )
  }

  if (isPriceRatesLoading) {
    return (
      <S.Container>
        <S.DefaultPricing>
          <Skeleton.Shape height={42} width={400} />
        </S.DefaultPricing>
      </S.Container>
    )
  }

  if (!priceRates)
    return (
      <S.Container>
        <Banner
          appearance="danger"
          title="We couldn't load exchange rates. Try refreshing the page."
        />
      </S.Container>
    )

  return (
    <S.Container>
      <S.DefaultPricing>
        <p>
          Based on <TimezonedDateString date={dayjs()} format="MMM D, YYYY" /> exchange rate:
        </p>
        {enabledCurrencies
          .map((currency) => `${currency} ${getExchangeRate(currency, priceRates)}`)
          .join(', ')}
      </S.DefaultPricing>
    </S.Container>
  )
}
